/** moderator/moderation — HOOKS. */
import { queryOptions, useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { moderationApi } from './api';
import { moderationKeys } from './keys';
import type { BanPlayerVars, EndPrivateChatVars, ResolveHelpVars, StartPrivateChatVars } from './types';

/** How often the moderator's match list reads again on its own, in seconds; the screen says it. */
export const MATCH_LIST_POLL_SECONDS = 10;

/** Reads the moderator's data again — the match list and an open player card — after an action changes it. */
const invalidateModeration = (qc: QueryClient) => qc.invalidateQueries({ queryKey: moderationKeys.all() });

/**
 * Every match the game server runs, read again every MATCH_LIST_POLL_SECONDS
 * while the screen is shown, on Refresh (refetch), and after a moderator's
 * action (moderationActions). A read that fails keeps the list last read — the query keeps
 * its data through a failed read — and says so (isError); it is not retried at
 * once, as the poll is its retry, which the screen says. It is stale as soon
 * as it is read, so a moderator coming back to the screen reads it again.
 */
export const moderationMatchesQuery = queryOptions({
  queryKey: moderationKeys.matches(),
  queryFn: moderationApi.listMatches,
  refetchInterval: MATCH_LIST_POLL_SECONDS * 1000,
  staleTime: 0,
  retry: false,
});

/** The moderator's match list (moderationMatchesQuery). */
export function useModerationMatches() {
  return useQuery(moderationMatchesQuery);
}

/** A player's details as the game server has them now, read when their card opens and after a moderator's action. */
export const playerDetailsQuery = (customId: string) =>
  queryOptions({
    queryKey: moderationKeys.player(customId),
    queryFn: () => moderationApi.getPlayerDetails(customId),
    staleTime: 0,
    retry: false,
  });

/**
 * How long the open card takes the click's read of its player as fresh. The
 * screen holds the card's query from the start, switched off; it switches to
 * the player as the card opens, just after the click has read them
 * (useReadPlayerDetails), and, stale at once, it read them again — each read
 * signalling every running match. Invalidated after an action, or on the
 * window's focus, it reads again whatever this says.
 */
const PLAYER_CARD_FRESH_MS = 5_000;

/**
 * The open player card's query (playerDetailsQuery), switched off while no
 * card is open (null), and opening on the click's read (PLAYER_CARD_FRESH_MS).
 */
export const playerCardQuery = (customId: string | null) => ({
  ...playerDetailsQuery(customId ?? ''),
  enabled: customId !== null,
  staleTime: PLAYER_CARD_FRESH_MS,
});

/**
 * The open player card's player (playerCardQuery), or nothing while no card is
 * open. The card draws from it, so a moderator's action, which reads the
 * moderator's data again, shows on the open card.
 */
export function usePlayerDetails(customId: string | null) {
  return useQuery(playerCardQuery(customId));
}

/**
 * Reads a player's details into the cache before their card opens: a function
 * the click calls, failing as the read fails, so a read that cannot be made is
 * said (a toast) rather than drawn as an empty card. The card itself draws
 * from usePlayerDetails.
 */
export function useReadPlayerDetails() {
  const qc = useQueryClient();
  return (customId: string) => qc.fetchQuery(playerDetailsQuery(customId));
}

/**
 * The moderator's actions on a player's match, each reading the moderator's
 * data again once it answers — the match list and an open player card — so
 * what it did shows: a hand lowered, a player banned, in a private chat or
 * out of one. A ban and a resolved hand settle once that read is in, so their
 * buttons stay disabled until then; a private chat's start and end do not
 * wait on it, as its window opens, or closes, on the answer. The private
 * chat's two had read nothing again, so the list showed a player in a
 * private chat, or out of one, only at its next poll.
 */
export const moderationActions = (qc: QueryClient) => ({
  resolveHelp: {
    mutationFn: (v: ResolveHelpVars) => moderationApi.resolveHelp(v.userId, v.matchId),
    onSuccess: () => invalidateModeration(qc),
  },
  banPlayer: {
    mutationFn: (v: BanPlayerVars) => moderationApi.banPlayer(v.playerCustomId, v.matchId, v.reason),
    onSuccess: () => invalidateModeration(qc),
  },
  startPrivateChat: {
    mutationFn: (v: StartPrivateChatVars) =>
      moderationApi.startPrivateChat(v.playerCustomId, v.gameId, v.worldId, v.cohortId, v.matchId),
    onSuccess: () => {
      void invalidateModeration(qc);
    },
  },
  endPrivateChat: {
    mutationFn: (v: EndPrivateChatVars) => moderationApi.endPrivateChat(v.playerCustomId, v.matchId, v.reason),
    onSuccess: () => {
      void invalidateModeration(qc);
    },
  },
});

/** Clears a player's raised hand (moderationActions). */
export function useResolveHelp() {
  return useMutation(moderationActions(useQueryClient()).resolveHelp);
}

/** Bans a player from their match (moderationActions). */
export function useBanPlayer() {
  return useMutation(moderationActions(useQueryClient()).banPlayer);
}

/** Opens a private chat with a player, inviting them through their match (moderationActions). */
export function useStartPrivateChat() {
  return useMutation(moderationActions(useQueryClient()).startPrivateChat);
}

/** Ends a private chat, telling the player through their match (moderationActions). */
export function useEndPrivateChat() {
  return useMutation(moderationActions(useQueryClient()).endPrivateChat);
}

/** A fresh scoped credential for one monitor connection; it is never cached or retried. */
export function useVoiceMonitorToken() {
  return useMutation({ mutationFn: moderationApi.voiceMonitorToken, retry: false, gcTime: 0 });
}
